import { useEffect } from 'react';
import { Typography, Box } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { CheckboxGroupInput, TextInput, maxLength, useTranslate, required, NumberInput } from 'react-admin';
import { operatorChoices } from '@/components/global/options/selectOptions.jsx';
import { ContractorEditServiceCompanies } from '@/components/contractors/ContractorEditServiceCompanies.jsx';
import { ContractorEditDiscountRanges } from '@/components/contractors/ContractorEditDiscountRanges.jsx';
import { DepositWithdrawalMethodsField, SettingsCheckboxGroup, SettingsSection } from '@/components/global/forms/SettingsSection.jsx';

const OperatorIdInput = () => {
    const translate = useTranslate();
    const selectedOperators = useWatch({ name: 'operators' });
    const matchingOperatorsOption = selectedOperators ? selectedOperators.includes('Reselekt') : null;

    return (
        matchingOperatorsOption && (
            <>
                <Typography variant='h6' gutterBottom>
                    {translate('resources.contractor.labels.operatorId')}
                </Typography>
                <TextInput
                    source='operatorId'
                    label={translate('resources.contractor.labels.operatorId')}
                    fullWidth
                    validate={[maxLength(150, translate('common.validation.maxLength', { count: 150 })), required(translate('ra.validation.required'))]}
                />
            </>
        )
    );
};

export const ContractorEditSettings = () => {
    const { setValue } = useFormContext();
    const translate = useTranslate();

    useEffect(() => {
        setValue('posTypes', [], { shouldDirty: true });
    }, [setValue]);

    const handleChangeOperators = operators => {
        if (!operators.includes('Reselekt')) {
            setValue('operatorId', null);
        }
    };

    return (
        <SettingsSection title={translate('common.settings')}>
            <Box className='settings-split'>
                <Box>
                    <SettingsCheckboxGroup>
                        <CheckboxGroupInput
                            source='operators'
                            label={translate('resources.contractor.fields.operators').toUpperCase()}
                            choices={operatorChoices}
                            row={false}
                            defaultValue={[]}
                            onChange={handleChangeOperators}
                        />
                    </SettingsCheckboxGroup>
                </Box>
                <Box>
                    <DepositWithdrawalMethodsField label={translate('analytics.transaction.payoutForms').toUpperCase()} />
                </Box>
            </Box>
            <Box sx={{ margin: '24px 0 28px' }}>
                <OperatorIdInput />
            </Box>
            <Typography variant='h6' gutterBottom>
                {translate('resources.contractor.labels.defaultValuesForOnePackage').toUpperCase()}
            </Typography>
            <Box className='settings-values'>
                <Box>
                    <NumberInput
                        source='bottleDepositValue'
                        label={translate('resources.contractor.labels.defaultBottleDepositValue')}
                        fullWidth
                        defaultValue={0.5}
                        min={0}
                        max={9999999}
                        step={0.01}
                    />
                </Box>
                <Box>
                    <NumberInput
                        source='loyaltyPointsPerBottle'
                        label={translate('resources.contractor.labels.defaultLoyaltyPointsPerBottle')}
                        fullWidth
                        placeholder={translate('resources.contractor.labels.defaultLoyaltyPointsPerBottlePlaceholder')}
                        min={0}
                        max={9999999}
                        step={1}
                    />
                </Box>
            </Box>
            <ContractorEditDiscountRanges />
            <ContractorEditServiceCompanies />
            <Box className='contractor-edit-grid'>
                <TextInput
                    label={translate('resources.contractor.labels.description')}
                    source='description'
                    fullWidth
                    multiline
                    validate={[maxLength(100, translate('common.validation.maxLength', { count: 100 }))]}
                    defaultValue=''
                />
            </Box>
        </SettingsSection>
    );
};
